import { EmptyState, Pagination, SelectField, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import {
	IMPORT_COUNT_KEYS,
	type ImportChangeRowView,
	type ImportExecutionSummaryView,
	type ImportValidationErrorRowView,
	type Option,
	type Translate,
} from './import-monitoring-types'

/**
 * Props for {@link ImportExecutionDetailView}.
 *
 * @property summary - The execution, or `null` while loading / after an error.
 * @property onDownloadErrors - Present only when the errors report exists; absent hides the button.
 * @property onDownloadOriginal - Present only when the viewer may download the original file and it is
 *   available; absent hides the button.
 * @property downloadFailed - True after a link request failed: renders the failure message.
 */
export interface ImportExecutionDetailViewProps {
	t: Translate
	summary: ImportExecutionSummaryView | null
	error: boolean
	validationErrors: ImportValidationErrorRowView[]
	changes: ImportChangeRowView[]
	changesPage: number
	changesPageCount: number
	actionFilter: string
	actionOptions: Option[]
	outcomeFilter: string
	outcomeOptions: Option[]
	downloadFailed: boolean
	onActionChange: (action: string) => void
	onOutcomeChange: (outcome: string) => void
	onChangesPageChange: (page: number) => void
	onDownloadErrors?: () => void
	onDownloadOriginal?: () => void
	onBack: () => void
	onRetry: () => void
}

const errorColumns = (t: Translate): TableColumn<ImportValidationErrorRowView>[] => [
	{ key: 'recordNumber', header: t('detail.errors.record') },
	{ key: 'stageLabel', header: t('detail.errors.stage') },
	{ key: 'code', header: t('detail.errors.code') },
	{ key: 'field', header: t('detail.errors.field') },
]

const changeColumns = (t: Translate): TableColumn<ImportChangeRowView>[] => [
	{ key: 'seq', header: t('detail.changes.seq') },
	{ key: 'sourceLine', header: t('detail.changes.line') },
	{ key: 'actionLabel', header: t('detail.changes.action') },
	{ key: 'key', header: t('detail.changes.key') },
	{ key: 'diff', header: t('detail.changes.diff') },
	{ key: 'outcomeLabel', header: t('detail.changes.outcome') },
	{ key: 'errorCode', header: t('detail.changes.errorCode') },
]

function Facts({ t, summary }: { t: Translate; summary: ImportExecutionSummaryView }): ReactElement {
	const facts: [string, string][] = [
		[t('list.columns.logicalFile'), summary.logicalFileLabel],
		[t('list.columns.transport'), summary.transportLabel],
		[t('list.columns.definition'), summary.definition],
		[t('list.columns.received'), summary.receivedAt],
		[t('list.columns.status'), summary.statusLabel],
		[t('detail.failureCode'), summary.failureCode],
		[t('detail.sha256'), summary.sha256],
		[t('detail.validationOutcome'), summary.validationOutcomeLabel],
		[t('detail.failedStage'), summary.failedStageLabel],
		[t('detail.totalErrors'), summary.totalErrors],
		...IMPORT_COUNT_KEYS.map((count): [string, string] => [t(`counts.${count}`), summary.counts[count]]),
	]
	return (
		<dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-ds-sm text-ds-text">
			{facts.map(([label, value]) => (
				<div key={label} className="flex flex-col">
					<dt className="font-semibold">{label}</dt>
					<dd>{value}</dd>
				</div>
			))}
		</dl>
	)
}

function Downloads(props: ImportExecutionDetailViewProps): ReactElement {
	const { t } = props
	return (
		<>
			<div className="flex flex-wrap gap-4">
				{props.onDownloadErrors ? <Button onPress={props.onDownloadErrors}>{t('detail.downloadErrors')}</Button> : null}
				{props.onDownloadOriginal ? (
					<Button variant="muted" onPress={props.onDownloadOriginal}>
						{t('detail.downloadOriginal')}
					</Button>
				) : null}
			</div>
			{props.downloadFailed ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{t('detail.downloadFailed')}
				</p>
			) : null}
		</>
	)
}

function ChangeLog(props: ImportExecutionDetailViewProps): ReactElement {
	const { t } = props
	return (
		<>
			<h3 className="text-ds-base font-semibold text-ds-text">{t('detail.changes.title')}</h3>
			<div className="flex flex-wrap gap-4">
				<SelectField
					label={t('detail.changes.action')}
					value={props.actionFilter}
					onValueChange={props.onActionChange}
					options={props.actionOptions.map((o) => ({ id: o.id, text: o.label }))}
				/>
				<SelectField
					label={t('detail.changes.outcome')}
					value={props.outcomeFilter}
					onValueChange={props.onOutcomeChange}
					options={props.outcomeOptions.map((o) => ({ id: o.id, text: o.label }))}
				/>
			</div>
			<Table
				aria-label={t('detail.changes.title')}
				columns={changeColumns(t)}
				rows={props.changes}
				emptyState={<EmptyState title={t('detail.changes.empty')} />}
			/>
			<Pagination
				page={props.changesPage}
				pageCount={props.changesPageCount}
				onPageChange={props.onChangesPageChange}
			/>
		</>
	)
}

/** One import execution: facts and counts, validation errors, downloads and the paged change log (organism). */
export function ImportExecutionDetailView(props: ImportExecutionDetailViewProps): ReactElement {
	const { t, summary } = props
	if (props.error) {
		return <EmptyState title={t('detail.error')} action={{ label: t('common.retry'), onPress: props.onRetry }} />
	}
	if (!summary) return <p className="text-ds-sm text-ds-text">{t('common.loading')}</p>
	return (
		<section className="flex flex-col gap-6">
			<div className="flex items-center gap-4">
				<Button variant="muted" size="sm" onPress={props.onBack}>
					{t('detail.back')}
				</Button>
				<h2 className="text-ds-lg font-semibold text-ds-text">{summary.fileName}</h2>
			</div>
			<Facts t={t} summary={summary} />
			<Downloads {...props} />
			<h3 className="text-ds-base font-semibold text-ds-text">{t('detail.errors.title')}</h3>
			<Table
				aria-label={t('detail.errors.title')}
				columns={errorColumns(t)}
				rows={props.validationErrors}
				emptyState={<EmptyState title={t('detail.errors.empty')} />}
			/>
			<ChangeLog {...props} />
		</section>
	)
}
